import { downloadFile } from '../creatives/data/csv.js';
import { chartValueText, rowValueText } from './chartFormat.js';
import { type Finding, type InsightsChart, type ReportResult, scopeText } from './reportResult.js';

function present(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== '';
}

function findingLine(finding: Finding): string {
  const said = [finding.evidence, finding.context].filter(present);
  return said.length === 0
    ? `- **${finding.title}**`
    : `- **${finding.title}** — ${said.join(' ')}`;
}

function chartLines(chart: InsightsChart): string[] {
  const lines = [`### ${chart.title}`];
  if (present(chart.subtitle)) lines.push(chart.subtitle);
  if (present(chart.unavailable_reason)) return [...lines, `_${chart.unavailable_reason}_`, ''];
  lines.push('');
  for (const row of chart.rows) {
    const detail = present(row.detail) ? ` (${row.detail})` : '';
    lines.push(`- ${row.label}${detail}: ${rowValueText(chart.unit, row.value)}`);
  }
  const notes = [chart.baseline, ...chart.stats].flatMap((value) =>
    value === null || value === undefined ? [] : [chartValueText(value)],
  );
  if (notes.length > 0) lines.push('', ...notes);
  return [...lines, ''];
}

/** The report as Markdown, under the service's own headings (ActionDisplay.labels). */
export function reportMarkdown(labels: Record<string, string>, result: ReportResult): string {
  const { report, scope, charts } = result;
  const lines = [`# ${labels['panel_title'] ?? ''}`.trimEnd(), ''];
  if (present(report.summary)) {
    if (present(labels['overview'])) lines.push(`## ${labels['overview']}`);
    lines.push(report.summary);
    const covered = scope === null ? null : scopeText(scope);
    if (covered !== null) lines.push(covered);
    lines.push('');
  }
  if (charts.length > 0 || report.key_findings.length > 0) {
    if (present(labels['findings'])) lines.push(`## ${labels['findings']}`, '');
    for (const chart of charts) lines.push(...chartLines(chart));
    for (const finding of report.key_findings) lines.push(findingLine(finding));
    lines.push('');
  }
  if (report.recommendations.length > 0) {
    if (present(labels['recommendations'])) lines.push(`## ${labels['recommendations']}`, '');
    report.recommendations.forEach((recommendation, index) => {
      const priority = present(recommendation.priority) ? ` (${recommendation.priority})` : '';
      const why = [recommendation.rationale, recommendation.expected_impact].filter(present);
      const reason = why.length === 0 ? '' : ` — ${why.join(' ')}`;
      lines.push(`${index + 1}. **${recommendation.action}**${priority}${reason}`);
    });
    lines.push('');
  }
  return lines.join('\n');
}

export function downloadReport(labels: Record<string, string>, result: ReportResult): void {
  const name = labels['panel_title'] ?? 'report';
  downloadFile(`${name}.md`, reportMarkdown(labels, result), 'text/markdown');
}
